// Schedule — day/week calendar + appointment-detail modal (D2/D3). Faithful port of Luan's doctor
// portal: .view-toggle / .day-group / .appt rows, and the appointment-detail modal (detail →
// reschedule 14-day scroller + time-slot grid → cancel-with-reason). Thin labels only — no record
// content anywhere (D5/D10). Real data + real mutations: api.appointment / api.reschedule / api.cancel.
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Modal, classifyVisit } from "@championcare/ui";
import { useApi } from "../api";
import { useAsync } from "../lib/useAsync";
import { formatTime, isThisWeek, isToday, labelText } from "../lib/format";
import { Loading, ErrorState } from "../components/ui";
import type { QueueItem, RosterPatient } from "@forcebase-ai/schema/doctor";
import "../styles-schedule.css";

const DAY_NAMES_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_NAMES_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const SLOT_MINUTES = 30;
const DAY_START_MIN = 8 * 60; // 8:00 AM
const DAY_END_MIN = 16 * 60 + 30; // 4:30 PM
const LUNCH_HOUR = 12;

function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}
function avgDuration(items: QueueItem[]): number | null {
  if (!items.length) return null;
  return Math.round(items.reduce((s, a) => s + (a.duration_minutes || 20), 0) / items.length);
}
function minutesUntil(iso: string): number {
  return Math.round((new Date(iso).getTime() - Date.now()) / 60000);
}
/** Human-readable "next appointment" label: relative when soon ("in 45 min" / "in 2 hours"),
 * weekday or date + clock time when further out ("Tomorrow, 2:30 PM" / "Tue, 2:30 PM"). */
function formatNextAppt(iso: string): string {
  const m = minutesUntil(iso);
  if (m < 60) return `in ${Math.max(0, m)} min`;
  if (m < 8 * 60) {
    const h = Math.round(m / 60);
    return `in ${h} hour${h === 1 ? "" : "s"}`;
  }
  const d = new Date(iso);
  const days = offsetForDate(iso);
  if (days === 0) return `Today, ${formatTime(iso)}`;
  if (days === 1) return `Tomorrow, ${formatTime(iso)}`;
  if (days < 7)
    return `${DAY_NAMES_SHORT[d.getDay()]} ${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}, ${formatTime(iso)}`;
  return `${MONTH_SHORT[d.getMonth()]} ${d.getDate()}, ${formatTime(iso)}`;
}
function initials(p?: RosterPatient): string {
  if (!p) return "··";
  return `${p.first_name.charAt(0)}${p.last_name.charAt(0)}`.toUpperCase();
}
function isWeekend(d: Date): boolean {
  return d.getDay() === 0 || d.getDay() === 6;
}
function startOfDay(d: Date): Date {
  const n = new Date(d);
  n.setHours(0, 0, 0, 0);
  return n;
}
function dateForOffset(offset: number): Date {
  const d = startOfDay(new Date());
  d.setDate(d.getDate() + offset);
  return d;
}
function offsetForDate(iso: string): number {
  const today = startOfDay(new Date()).getTime();
  const d = startOfDay(new Date(iso)).getTime();
  return Math.round((d - today) / 86400000);
}
function sameDate(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
/** "8:00 AM" style label for a minutes-since-midnight value. */
function slotLabel(totalMin: number): string {
  const h24 = Math.floor(totalMin / 60);
  const min = totalMin % 60;
  const period = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
  return `${h12}:${String(min).padStart(2, "0")} ${period}`;
}
function minutesOfDay(iso: string): number {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
}
function dayGroupLabel(offset: number): string {
  const d = dateForOffset(offset);
  if (offset === 0) return `Today, ${DAY_NAMES_SHORT[d.getDay()]} ${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`;
  if (offset === 1) return `Tomorrow, ${DAY_NAMES_SHORT[d.getDay()]} ${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`;
  return `${DAY_NAMES_FULL[d.getDay()]}, ${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`;
}

// --- Icons (lifted from Luan; thin stroke) ---------------------------------
const JoinIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);
const SearchIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const CalendarIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const RescheduleIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);
const CloseIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const BackIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const ForwardIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const DEFAULT_CANCEL_REASON = "Patient requested cancellation";
const CANCEL_REASONS: string[] = [
  DEFAULT_CANCEL_REASON,
  "Provider unavailable",
  "Rescheduling to different time",
  "Patient no-show",
  "Patient illness",
  "Other",
];

type ModalView = "detail" | "reschedule" | "cancel";

export function Schedule() {
  const api = useApi();
  const navigate = useNavigate();
  const me = useAsync(() => api.me(), []);
  const queue = useAsync(() => api.queue(), []);
  const patients = useAsync(() => api.patients(), []);
  const [q, setQ] = useState("");
  const [view, setView] = useState<"day" | "week">("day");
  const [weekOffset, setWeekOffset] = useState(0); // 0 = current calendar week; ±N pages weeks
  const [showCompleted, setShowCompleted] = useState(false); // hide finished visits from the schedule by default

  // Modal state — the selected appointment and which sub-view is showing.
  const [selected, setSelected] = useState<QueueItem | null>(null);
  const [modalView, setModalView] = useState<ModalView>("detail");
  const [rsOffset, setRsOffset] = useState<number | null>(null);
  const [rsTime, setRsTime] = useState<number | null>(null); // minutes-of-day
  const [cancelReason, setCancelReason] = useState(DEFAULT_CANCEL_REASON);
  const [cancelNote, setCancelNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const items = queue.data ?? [];
  // "Show completed" toggle (default off): finished visits are terminal, not actionable, so they're
  // hidden from the day/week lists until the doctor opts in — otherwise the schedule fills with
  // "Start visit" rows for visits that already happened. (Conflict detection below still uses the
  // full set.) Shared `classifyVisit` keeps this in lockstep with the patient Visits buckets.
  const visibleItems = showCompleted ? items : items.filter((a) => classifyVisit(a) !== "completed");
  const roster = useMemo(
    () => new Map((patients.data ?? []).map((p) => [p.id, p])),
    [patients.data],
  );

  if (queue.loading || me.loading) return <Loading />;
  if (queue.error) return <ErrorState message={queue.error} />;

  const nameOf = (id: string) => {
    const p = roster.get(id);
    return p ? `${p.first_name} ${p.last_name}` : "Patient";
  };

  const today = visibleItems
    .filter((a) => isToday(a.scheduled_at))
    .sort((a, b) => +new Date(a.scheduled_at) - +new Date(b.scheduled_at));
  const week = visibleItems.filter((a) => isThisWeek(a.scheduled_at));
  // Every future visit that isn't already in today's column. Patients can book up to ~60 days out,
  // but the day/week views only reach the next 7 days — without this an upcoming booking stays
  // invisible to the doctor until its day arrives. Surfaced in the aside so it always shows.
  const upcoming = [...visibleItems]
    .filter((a) => new Date(a.scheduled_at).getTime() > Date.now() && !isToday(a.scheduled_at))
    .sort((a, b) => +new Date(a.scheduled_at) - +new Date(b.scheduled_at));
  const next = upcoming[0] ?? today.find((a) => new Date(a.scheduled_at).getTime() > Date.now());
  const avg = avgDuration(visibleItems);

  const matchesQuery = (a: QueueItem) =>
    !q.trim() || nameOf(a.patient_id).toLowerCase().includes(q.trim().toLowerCase());

  const dateLabel = new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });

  // Week view: the calendar week (Sun–Sat) containing today, shifted by weekOffset so the doctor can
  // page through past/future weeks. Weekends are INCLUDED — a weekend booking must never be hidden.
  const todayDow = startOfDay(new Date()).getDay();
  const weekStartOff = -todayDow + weekOffset * 7;
  const weekGroups: { offset: number; appts: QueueItem[] }[] = [];
  if (view === "week") {
    for (let i = 0; i <= 6; i++) {
      const o = weekStartOff + i;
      weekGroups.push({
        offset: o,
        appts: visibleItems
          .filter((a) => offsetForDate(a.scheduled_at) === o && matchesQuery(a))
          .sort((a, b) => +new Date(a.scheduled_at) - +new Date(b.scheduled_at)),
      });
    }
  }
  const weekApptCount = weekGroups.reduce((s, g) => s + g.appts.length, 0);
  const wkStart = dateForOffset(weekStartOff);
  const wkEnd = dateForOffset(weekStartOff + 6);
  const weekRangeLabel =
    wkStart.getMonth() === wkEnd.getMonth()
      ? `${MONTH_SHORT[wkStart.getMonth()]} ${wkStart.getDate()}–${wkEnd.getDate()}`
      : `${MONTH_SHORT[wkStart.getMonth()]} ${wkStart.getDate()} – ${MONTH_SHORT[wkEnd.getMonth()]} ${wkEnd.getDate()}`;
  const weekTitle =
    weekOffset === 0
      ? "This week"
      : weekOffset === 1
        ? "Next week"
        : weekOffset === -1
          ? "Last week"
          : weekRangeLabel;

  const dayAppts = today.filter(matchesQuery);

  // --- Modal helpers -------------------------------------------------------
  // Start visit: open the Zoom HOST link in a new tab, then send the doctor to the patient's chart
  // (instead of the in-app visit room). The tab is opened synchronously inside the click gesture so
  // the host start_url isn't popup-blocked after the async join fetch — mirrors VisitScreen.start().
  function startVisit(a: QueueItem) {
    const tab = window.open("", "_blank");
    api
      .joinVisitHost(a.id)
      .then((t) => {
        if (t.startUrl) {
          if (tab) tab.location.href = t.startUrl;
          else window.open(t.startUrl, "_blank", "noopener");
        } else {
          tab?.close();
        }
      })
      .catch(() => tab?.close());
    navigate(`/patients/${a.patient_id}`);
  }

  function openAppt(a: QueueItem) {
    setSelected(a);
    setModalView("detail");
    setRsOffset(null);
    setRsTime(null);
    setCancelReason(DEFAULT_CANCEL_REASON);
    setCancelNote("");
    setActionError(null);
  }
  function closeModal() {
    setSelected(null);
    setBusy(false);
  }
  function refreshAndClose() {
    queue.reload();
    closeModal();
  }

  // 14 working days from today (skip weekends).
  function rescheduleDateOptions(): number[] {
    const out: number[] = [];
    let o = 0;
    while (out.length < 14 && o < 40) {
      if (!isWeekend(dateForOffset(o))) out.push(o);
      o++;
    }
    return out;
  }

  type Slot = { min: number; status: "available" | "disabled"; label?: string; current?: boolean };
  function timeSlotsForOffset(offset: number, appt: QueueItem): Slot[] {
    const slots: Slot[] = [];
    const apptOffset = offsetForDate(appt.scheduled_at);
    const apptMin = minutesOfDay(appt.scheduled_at);
    for (let total = DAY_START_MIN; total <= DAY_END_MIN; total += SLOT_MINUTES) {
      const h24 = Math.floor(total / 60);
      if (h24 === LUNCH_HOUR) {
        slots.push({ min: total, status: "disabled", label: "Lunch" });
        continue;
      }
      // Conflicting appointment overlapping this 30-min window (excluding the one being moved).
      const conflict = items.find((x) => {
        if (x.id === appt.id) return false;
        if (offsetForDate(x.scheduled_at) !== offset) return false;
        const xStart = minutesOfDay(x.scheduled_at);
        const xEnd = xStart + (x.duration_minutes || SLOT_MINUTES);
        return total < xEnd && total + SLOT_MINUTES > xStart;
      });
      if (conflict) {
        slots.push({ min: total, status: "disabled", label: initials(roster.get(conflict.patient_id)) });
        continue;
      }
      const current = apptOffset === offset && apptMin === total;
      slots.push({ min: total, status: "available", current });
    }
    return slots;
  }

  async function confirmReschedule() {
    if (!selected || rsOffset === null || rsTime === null) return;
    setBusy(true);
    setActionError(null);
    const when = startOfDay(dateForOffset(rsOffset));
    when.setMinutes(rsTime); // rsTime is minutes-of-day from a midnight base
    try {
      await api.reschedule(selected.id, {
        new_scheduled_at: when.toISOString(),
        reason: "Provider rescheduled appointment",
      });
      refreshAndClose();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Could not reschedule");
      setBusy(false);
    }
  }

  async function confirmCancel() {
    if (!selected) return;
    setBusy(true);
    setActionError(null);
    const reason = cancelNote.trim() ? `${cancelReason} — ${cancelNote.trim()}` : cancelReason;
    try {
      await api.cancel(selected.id, { reason });
      refreshAndClose();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Could not cancel");
      setBusy(false);
    }
  }

  // --- Appointment row -----------------------------------------------------
  const ApptRow = ({ a }: { a: QueueItem }) => (
    <div className="appt" onClick={() => openAppt(a)}>
      <div className="appt-time">
        <div className="time">{formatTime(a.scheduled_at)}</div>
        <div className="duration">{a.duration_minutes} min</div>
      </div>
      <div className="appt-body">
        <div className="avatar">{initials(roster.get(a.patient_id))}</div>
        <div className="appt-info">
          <div className="patient">{nameOf(a.patient_id)}</div>
          <div className="reason">{labelText(a.label)}</div>
          <div className="tags">
            <span className="tag">{labelText(a.label)}</span>
          </div>
        </div>
      </div>
      <div className="appt-actions" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="btn btn-zoom btn-sm" onClick={() => startVisit(a)}>
          {JoinIcon} Start visit
        </button>
      </div>
    </div>
  );

  const rsOptions = selected ? rescheduleDateOptions() : [];
  const effectiveRsOffset = rsOffset ?? (selected ? offsetForDate(selected.scheduled_at) : 0);
  const slots = selected ? timeSlotsForOffset(effectiveRsOffset, selected) : [];
  const canConfirmRs =
    selected != null &&
    rsTime != null &&
    (effectiveRsOffset !== offsetForDate(selected.scheduled_at) || rsTime !== minutesOfDay(selected.scheduled_at));

  return (
    <>
      <div className="dp-head">
        <div>
          <div className="dp-head__greet">
            {greeting()}, Dr. {me.data?.last_name ?? me.data?.first_name ?? "Provider"}
          </div>
          <h1 className="dp-head__title">
            Today&rsquo;s <em>schedule.</em>
          </h1>
        </div>
        <div className="dp-search">
          {SearchIcon}
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search patients…" aria-label="Search patients" />
        </div>
      </div>

      <div className="dp-stats">
        <div className="dp-stat dp-stat--accent">
          <div className="dp-stat__label">Today&rsquo;s visits</div>
          <div className="dp-stat__value">{today.length}</div>
          <div className="dp-stat__sub">scheduled today</div>
        </div>
        <div className="dp-stat">
          <div className="dp-stat__label">This week</div>
          <div className="dp-stat__value">{week.length}</div>
          <div className="dp-stat__sub">across clinic days</div>
        </div>
        <div className="dp-stat">
          <div className="dp-stat__label">Patient panel</div>
          <div className="dp-stat__value">{patients.data?.length ?? "—"}</div>
          <div className="dp-stat__sub">members</div>
        </div>
        <div className="dp-stat">
          <div className="dp-stat__label">Avg visit length</div>
          <div className="dp-stat__value">{avg ? `${avg}` : "—"}</div>
          <div className="dp-stat__sub">minutes</div>
        </div>
      </div>

      <div className="dp-grid">
        <div className="dp-card">
          <div className="dp-card__head">
            {view === "day" ? (
              <span className="dp-card__title">
                Today, <em>{dateLabel}</em>
              </span>
            ) : (
              <span className="dp-card__title">
                Week <em>view</em>
              </span>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button
                type="button"
                className={`view-toggle-btn ${showCompleted ? "active" : ""}`}
                onClick={() => setShowCompleted((s) => !s)}
                aria-pressed={showCompleted}
                title="Toggle finished visits in the schedule"
              >
                {showCompleted ? "Hide completed" : "Show completed"}
              </button>
              <div className="view-toggle" role="tablist" aria-label="Calendar view">
                <button
                  className={`view-toggle-btn ${view === "day" ? "active" : ""}`}
                  onClick={() => setView("day")}
                  role="tab"
                  aria-selected={view === "day"}
                >
                  Day
                </button>
                <button
                  className={`view-toggle-btn ${view === "week" ? "active" : ""}`}
                  onClick={() => setView("week")}
                  role="tab"
                  aria-selected={view === "week"}
                >
                  Week
                </button>
              </div>
            </div>
          </div>

          {view === "week" && (
            <div
              className="week-nav"
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, margin: "2px 0 16px" }}
            >
              <button className="btn btn-ghost btn-sm" onClick={() => setWeekOffset((w) => w - 1)} aria-label="Previous week">
                {BackIcon} Prev
              </button>
              <div style={{ textAlign: "center", lineHeight: 1.3 }}>
                <div style={{ fontWeight: 600 }}>{weekTitle}</div>
                <div style={{ fontSize: 12, color: "var(--muted)" }}>
                  {weekRangeLabel} · {weekApptCount} visit{weekApptCount === 1 ? "" : "s"}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {weekOffset !== 0 && (
                  <button className="btn btn-ghost btn-sm" onClick={() => setWeekOffset(0)}>
                    This week
                  </button>
                )}
                <button className="btn btn-ghost btn-sm" onClick={() => setWeekOffset((w) => w + 1)} aria-label="Next week">
                  Next {ForwardIcon}
                </button>
              </div>
            </div>
          )}

          {view === "day" ? (
            dayAppts.length ? (
              <div className="appt-list">
                {dayAppts.map((a) => (
                  <ApptRow key={a.id} a={a} />
                ))}
              </div>
            ) : (
              <div className="dp-empty">{q ? "No matching patients today." : "No visits today."}</div>
            )
          ) : weekApptCount ? (
            <div>
              {weekGroups.map((g) => (
                <div className={`day-group ${g.offset === 0 ? "is-today" : ""}`} key={g.offset}>
                  <div className="day-group-head">
                    <h4
                      dangerouslySetInnerHTML={{
                        __html:
                          g.offset === 0
                            ? `<em>Today</em>, ${DAY_NAMES_SHORT[dateForOffset(0).getDay()]} ${MONTH_SHORT[dateForOffset(0).getMonth()]} ${dateForOffset(0).getDate()}`
                            : dayGroupLabel(g.offset),
                      }}
                    />
                    <span className="count">
                      {g.appts.length} appointment{g.appts.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="appt-list">
                    {g.appts.length ? (
                      g.appts.map((a) => <ApptRow key={a.id} a={a} />)
                    ) : (
                      <div className="dp-empty">No visits.</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="dp-empty">{q ? "No matching patients this week." : "No visits this week."}</div>
          )}
        </div>

        <aside className="dp-card">
          <div className="dp-card__title" style={{ marginBottom: 14 }}>
            At a <em>glance</em>
          </div>
          <div className="dp-glance__row">
            <span className="dp-glance__k">Next appointment</span>
            <span className="dp-glance__v">{next ? formatNextAppt(next.scheduled_at) : "—"}</span>
          </div>
          <div className="dp-glance__row">
            <span className="dp-glance__k">Avg visit length</span>
            <span className="dp-glance__v">{avg ? `${avg} min` : "—"}</span>
          </div>
          <div className="dp-glance__row">
            <span className="dp-glance__k">This week</span>
            <span className="dp-glance__v">{week.length} visits</span>
          </div>

          <div className="dp-glance__title" style={{ margin: "18px 0 10px" }}>
            Upcoming <em>visits</em>
          </div>
          {upcoming.length ? (
            <div className="dp-upcoming">
              {upcoming.slice(0, 8).map((a) => (
                <button key={a.id} type="button" className="dp-upcoming__row" onClick={() => openAppt(a)}>
                  <span className="dp-upcoming__when">{formatNextAppt(a.scheduled_at)}</span>
                  <span className="dp-upcoming__who">{nameOf(a.patient_id)}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="dp-empty" style={{ padding: "8px 0" }}>No upcoming visits.</div>
          )}
        </aside>
      </div>

      {/* ===== Appointment detail / reschedule / cancel modal ===== */}
      <Modal open={selected != null} onClose={closeModal} wide labelledBy="apptDetailTitle">
        {selected && (
          <>
            <div className="sched-modal-title" id="apptDetailTitle">
              {modalView === "reschedule" ? (
                <>Reschedule <em>appointment</em></>
              ) : modalView === "cancel" ? (
                <>Cancel <em>appointment</em></>
              ) : (
                <>Appointment <em>details</em></>
              )}
            </div>
            <div className="sched-modal-sub">
              {nameOf(selected.patient_id)} · {DAY_NAMES_FULL[new Date(selected.scheduled_at).getDay()]},{" "}
              {MONTH_SHORT[new Date(selected.scheduled_at).getMonth()]} {new Date(selected.scheduled_at).getDate()} at{" "}
              {formatTime(selected.scheduled_at)}
            </div>

            {actionError && <ErrorState message={actionError} />}

            {modalView === "detail" && (
              <>
                <div className="appt-detail-head">
                  <div className="avatar lg">{initials(roster.get(selected.patient_id))}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3>
                      <Link className="appt-name-link" to={`/patients/${selected.patient_id}`} onClick={closeModal}>
                        {nameOf(selected.patient_id)}
                      </Link>
                    </h3>
                    <div className="meta">{labelText(selected.label)}</div>
                    <div className="tags">
                      <span className="tag">{labelText(selected.label)}</span>
                      <span className="tag">Video</span>
                    </div>
                  </div>
                </div>
                <div className="appt-info-grid">
                  <div>
                    <div className="label">Date</div>
                    <div className="value">
                      {DAY_NAMES_FULL[new Date(selected.scheduled_at).getDay()]},{" "}
                      {MONTH_SHORT[new Date(selected.scheduled_at).getMonth()]} {new Date(selected.scheduled_at).getDate()}
                    </div>
                  </div>
                  <div>
                    <div className="label">Time</div>
                    <div className="value">
                      {formatTime(selected.scheduled_at)} · {selected.duration_minutes} min
                    </div>
                  </div>
                  <div>
                    <div className="label">Visit type</div>
                    <div className="value">Video telehealth</div>
                  </div>
                  <div>
                    <div className="label">Status</div>
                    <div className="value" style={{ textTransform: "capitalize" }}>
                      {selected.status.replace(/_/g, " ")}
                    </div>
                  </div>
                  <div className="full">
                    <div className="label">Visit label</div>
                    <div className="value">{labelText(selected.label)}</div>
                  </div>
                </div>
                <div className="appt-quick-actions">
                  <button
                    type="button"
                    className="btn btn-zoom"
                    onClick={() => {
                      startVisit(selected);
                      closeModal();
                    }}
                  >
                    {JoinIcon} Start visit
                  </button>
                </div>
                <div className="appt-detail-foot appt-detail-foot--flush">
                  <button
                    className="btn-discontinue"
                    onClick={() => {
                      setActionError(null);
                      setModalView("cancel");
                    }}
                  >
                    {CloseIcon} Cancel appointment
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setActionError(null);
                      setRsOffset(offsetForDate(selected.scheduled_at));
                      setRsTime(null);
                      setModalView("reschedule");
                    }}
                  >
                    {RescheduleIcon} Reschedule
                  </button>
                </div>
              </>
            )}

            {modalView === "reschedule" && (
              <>
                <div className="reschedule-current">
                  <div className="icon">{CalendarIcon}</div>
                  <div>
                    <div className="label">Currently scheduled</div>
                    <div className="value">
                      {DAY_NAMES_FULL[new Date(selected.scheduled_at).getDay()]},{" "}
                      {MONTH_SHORT[new Date(selected.scheduled_at).getMonth()]} {new Date(selected.scheduled_at).getDate()} at{" "}
                      {formatTime(selected.scheduled_at)} · {selected.duration_minutes} min
                    </div>
                  </div>
                </div>

                <div className="reschedule-section">
                  <h4>
                    New date <span className="hint">scroll for more →</span>
                  </h4>
                  <div className="date-pills">
                    {rsOptions.map((o) => {
                      const d = dateForOffset(o);
                      const isSel = effectiveRsOffset === o;
                      const isTodayPill = sameDate(d, startOfDay(new Date()));
                      return (
                        <button
                          key={o}
                          type="button"
                          className={`date-pill ${isSel ? "selected" : ""}`}
                          onClick={() => {
                            setRsOffset(o);
                            setRsTime(null);
                          }}
                        >
                          <div className="day-name">{DAY_NAMES_SHORT[d.getDay()]}</div>
                          <div className="day-num">{d.getDate()}</div>
                          {isTodayPill ? (
                            <div className="today-tag">Today</div>
                          ) : (
                            <div className="today-tag" style={{ visibility: "hidden" }}>·</div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="reschedule-section">
                  <h4>
                    Available times{" "}
                    <span className="hint">
                      {MONTH_SHORT[dateForOffset(effectiveRsOffset).getMonth()]} {dateForOffset(effectiveRsOffset).getDate()}
                    </span>
                  </h4>
                  <div className="time-slots">
                    {slots.map((slot) => {
                      const isSel = rsTime === slot.min;
                      const cls = ["time-slot"];
                      if (slot.status === "disabled") cls.push("disabled");
                      if (isSel) cls.push("selected");
                      if (slot.current && !isSel) cls.push("current");
                      return (
                        <button
                          key={slot.min}
                          type="button"
                          className={cls.join(" ")}
                          disabled={slot.status === "disabled"}
                          onClick={slot.status === "available" ? () => setRsTime(slot.min) : undefined}
                        >
                          {slotLabel(slot.min)}
                          {slot.label ? (
                            <span className="conflict">{slot.label}</span>
                          ) : slot.current ? (
                            <span className="conflict">Current</span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="appt-detail-foot">
                  <button
                    className="btn btn-ghost"
                    onClick={() => {
                      setRsTime(null);
                      setModalView("detail");
                    }}
                  >
                    {BackIcon} Back
                  </button>
                  <div className="right">
                    <button className="btn btn-ghost" onClick={closeModal}>
                      Cancel
                    </button>
                    <button className="btn btn-primary" disabled={!canConfirmRs || busy} onClick={confirmReschedule}>
                      {busy ? "Rescheduling…" : "Confirm reschedule"}
                    </button>
                  </div>
                </div>
              </>
            )}

            {modalView === "cancel" && (
              <>
                <div style={{ textAlign: "center", padding: "8px 12px 4px" }}>
                  <div className="dc-icon">{CloseIcon}</div>
                  <p style={{ color: "var(--muted)", maxWidth: 420, margin: "0 auto 18px", lineHeight: 1.55, fontSize: 14 }}>
                    This visit will be removed from your schedule, and {nameOf(selected.patient_id)} will be notified.
                  </p>
                </div>
                <div className="dc-input" style={{ maxWidth: 440, margin: "0 auto" }}>
                  <label htmlFor="apptCancelReason">Reason for cancellation</label>
                  <select id="apptCancelReason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}>
                    {CANCEL_REASONS.map((r) => (
                      <option key={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div className="dc-input" style={{ maxWidth: 440, margin: "14px auto 0" }}>
                  <label htmlFor="apptCancelNote">Note (optional)</label>
                  <textarea
                    id="apptCancelNote"
                    value={cancelNote}
                    onChange={(e) => setCancelNote(e.target.value)}
                    placeholder="Additional context…"
                    style={{ minHeight: 60 }}
                  />
                </div>
                <div className="appt-detail-foot">
                  <button className="btn btn-ghost" onClick={() => setModalView("detail")}>
                    Keep appointment
                  </button>
                  <button className="btn-discontinue" disabled={busy} onClick={confirmCancel}>
                    {busy ? "Cancelling…" : "Cancel appointment"}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </Modal>
    </>
  );
}
